import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Map, SkipForward, Camera, Check } from 'lucide-react';
import BottomSheet from './BottomSheet';
import PrimaryButton from './PrimaryButton';
import { plantsApi, zonesApi, photosApi } from '../services/gardenApi';
import { convertHeicToJpeg, isHeicFile } from '../../utils/convertHeic';

function currentSeasonGuess() {
  const m = new Date().getMonth();
  if (m >= 2 && m <= 4) return 'spring';
  if (m >= 5 && m <= 7) return 'summer';
  if (m >= 8 && m <= 10) return 'fall';
  return 'winter';
}

// Shown after a plant is saved via any "Add plant" flow. Mobile-first, one
// clear action per step. Three possible paths:
//   - Select Zone:   pick from the user's zones; writes location_notes (no marker)
//   - Place on Map:  navigate into the zone detail so the user can tap-to-place
//   - Skip:          do nothing, just close
//
// After any choice we offer ONE optional photo of "where you planted it" which
// is uploaded straight to the plant's photo library.
export default function PlacementPromptSheet({ open, plant, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('choose');   // 'choose' | 'selectZone' | 'photo' | 'done'
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [zonesError, setZonesError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [photoDone, setPhotoDone] = useState(false);
  const photoInputRef = useRef(null);

  // Reset whenever the sheet is re-opened for a fresh save.
  useEffect(() => {
    if (!open) return;
    setStep('choose');
    setZones([]);
    setZonesError(null);
    setBusy(false);
    setPhotoBusy(false);
    setPhotoError(null);
    setPhotoDone(false);
  }, [open, plant?.id]);

  const loadZones = async () => {
    setZonesLoading(true);
    setZonesError(null);
    try {
      const r = await zonesApi.list();
      setZones(r.zones || []);
    } catch (e) {
      setZonesError(e.message || 'Could not load your zones');
    } finally {
      setZonesLoading(false);
    }
  };

  const goSelectZone = async () => {
    setStep('selectZone');
    await loadZones();
  };

  const chooseZone = async (zone) => {
    if (!plant || busy) return;
    setBusy(true);
    try {
      // MVP: record the zone as a location note so the plant is "assigned"
      // without forcing the user through the x/y map picker. They can still
      // drop a marker later from the zone detail screen.
      await plantsApi.patch(plant.id, { location_notes: zone.name });
      setStep('photo');
    } catch (e) {
      setZonesError(e.message || 'Could not save that zone');
    } finally {
      setBusy(false);
    }
  };

  const placeOnMap = () => {
    if (!plant) return;
    // Navigate to the zones home — the user already knows which zone they
    // want to open and can then use the existing "Add plant" placement flow
    // to drop their newly-saved plant onto the map.
    onClose?.();
    navigate('/garden');
  };

  const skipPlacement = () => {
    setStep('photo');
  };

  const handlePhotoPicked = async (e) => {
    let f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f || !plant) return;
    setPhotoError(null);

    if (isHeicFile(f)) {
      try {
        f = await convertHeicToJpeg(f);
      } catch (err) {
        setPhotoError(err.message || 'Could not convert HEIC image.');
        return;
      }
    }

    setPhotoBusy(true);
    try {
      await photosApi.upload(plant.id, f, { season: currentSeasonGuess() });
      setPhotoDone(true);
    } catch (err) {
      setPhotoError(err.message || 'Could not upload photo');
    } finally {
      setPhotoBusy(false);
    }
  };

  if (!plant) return null;

  const plantName = plant.common_name || 'This plant';

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={step === 'photo' ? 'Add a photo?' : 'Where is this planted?'}
    >
      {step === 'choose' ? (
        <div className="space-y-3 pt-1">
          <p className="text-sm text-garden-muted">
            &ldquo;{plantName}&rdquo; was saved to your library.
          </p>
          <PlacementChoice
            Icon={MapPin}
            title="Select zone"
            subtitle="Assign it to an area of your garden"
            onClick={goSelectZone}
          />
          <PlacementChoice
            Icon={Map}
            title="Place on map"
            subtitle="Drop a pin on your garden photo"
            onClick={placeOnMap}
          />
          <PlacementChoice
            Icon={SkipForward}
            title="Skip for now"
            subtitle="You can do this later"
            onClick={skipPlacement}
            muted
          />
        </div>
      ) : null}

      {step === 'selectZone' ? (
        <div className="space-y-2 pt-1">
          <p className="text-sm text-garden-muted mb-2">
            Pick the zone where &ldquo;{plantName}&rdquo; lives.
          </p>

          {zonesLoading ? (
            <div className="py-4 text-sm text-garden-muted">Loading zones{'\u2026'}</div>
          ) : zonesError ? (
            <div className="p-3 rounded-xl bg-red-50 text-sm text-red-700">{zonesError}</div>
          ) : zones.length === 0 ? (
            <div className="rounded-2xl bg-garden-primaryLight p-4 text-sm text-garden-primaryDark">
              You haven&rsquo;t added any zones yet. You can skip this for now
              and add zones from the Garden tab.
            </div>
          ) : (
            <ul className="divide-y divide-black/5 rounded-2xl border border-black/5 overflow-hidden bg-white">
              {zones.map((z) => (
                <li key={z.id}>
                  <button
                    type="button"
                    onClick={() => chooseZone(z)}
                    disabled={busy}
                    className="w-full text-left px-4 py-3 min-h-12 flex items-center justify-between disabled:opacity-50 active:bg-garden-primaryLight"
                  >
                    <span className="font-medium text-garden-ink truncate">{z.name}</span>
                    <MapPin size={16} className="text-garden-muted shrink-0 ml-2" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="pt-2 flex gap-2">
            <PrimaryButton
              variant="secondary"
              className="flex-1"
              onClick={() => setStep('choose')}
              disabled={busy}
            >
              Back
            </PrimaryButton>
            <PrimaryButton
              variant="secondary"
              className="flex-1"
              onClick={() => setStep('photo')}
              disabled={busy}
            >
              Skip
            </PrimaryButton>
          </div>
        </div>
      ) : null}

      {step === 'photo' ? (
        <div className="space-y-3 pt-1">
          <p className="text-sm text-garden-muted">
            Snap a quick photo of where you planted &ldquo;{plantName}&rdquo;? Optional.
          </p>

          <input
            ref={photoInputRef}
            type="file"
            accept="image/*,image/heic,image/heif,.heic,.heif"
            capture="environment"
            className="hidden"
            onChange={handlePhotoPicked}
          />

          {photoDone ? (
            <div className="rounded-2xl bg-garden-primaryLight p-4 flex items-center gap-3 text-sm text-garden-primaryDark font-medium">
              <Check size={18} />
              Photo saved to this plant.
            </div>
          ) : (
            <PrimaryButton
              onClick={() => photoInputRef.current?.click()}
              disabled={photoBusy}
              className="w-full"
            >
              <Camera size={18} /> {photoBusy ? 'Uploading\u2026' : 'Add photo'}
            </PrimaryButton>
          )}

          {photoError ? (
            <div className="p-3 rounded-xl bg-red-50 text-sm text-red-700">{photoError}</div>
          ) : null}

          <PrimaryButton
            variant="secondary"
            onClick={onClose}
            disabled={photoBusy}
            className="w-full"
          >
            {photoDone ? 'Done' : 'Skip'}
          </PrimaryButton>
        </div>
      ) : null}
    </BottomSheet>
  );
}

function PlacementChoice({ Icon, title, subtitle, onClick, muted }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-2xl bg-white border border-black/5 shadow-sm p-4 flex items-center gap-3 min-h-[72px] active:scale-[0.99] transition hover:border-garden-primary/30 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-garden-primary"
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${muted ? 'bg-black/5 text-garden-muted' : 'bg-garden-primaryLight text-garden-primaryDark'}`}>
        <Icon size={22} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-garden-ink">{title}</div>
        <div className="text-sm text-garden-muted truncate">{subtitle}</div>
      </div>
    </button>
  );
}
